import { AnchorHTMLAttributes } from "react";
import { Editor } from "slate";
import { Plugin } from "slate-react";
import { getNormalisedUrl, isTypicalUrl } from "@travellocal/utils/urlHelpers";
import { INLINES } from "../../html";
import { AddIsToggledQuery } from "../isToggled";
import * as LinkUtils from "./linkUtils";
import { renderLink } from "./renderLink";

/**
 * Properties we use on <a> tags
 */
export interface LinkAttributes extends Pick<AnchorHTMLAttributes<any>, "href" | "target"> {
  /**
   * Use this to signal to editTooltip that it should be in the expanded state when it opens.
   */
  isNew?: boolean;
}

const { type } = INLINES.LINK;

const isLink = (editor: Editor) => LinkUtils.getFocusedLink(editor.value) != null;

/**
 * Automatically linkify URLs inserted into the editor
 * Adapted from https://github.com/ianstormtaylor/slate-plugins/blob/master/packages/slate-paste-linkify/src/index.js
 */
const linkOnInsertPlugin: Plugin = {
  onCommand: (command, editor, next) => {
    // Get text from the paste command
    let text: string;
    if (command.type === "insertFragment") {
      text = command.args[0].text;
    }

    // Bail early if not good paste, to avoid running parse and regex below
    if (!text) {
      next();
      return;
    }

    // If it's not a typical URL, bail
    const href = getNormalisedUrl(text);
    if (!isTypicalUrl(href)) {
      next();
      return;
    }

    const { value } = editor;
    const { selection } = value;
    const { isCollapsed, start } = selection;

    // If no selection, paste text before linkifying
    if (isCollapsed) {
      // Let next action happen - this will be paste from clipboard
      next();

      // Create selection around the text we've just inserted
      editor
        .moveAnchorTo(start.offset)
        .moveFocusTo(start.offset + href.length);
    }

    LinkUtils.insertLink(editor, { href });
  },
};

/**
 * Linkify text that looks like a Url as it's written.
 * Adapted from example https://github.com/enzoferey/slate-instant-replace/blob/master/examples/urls-example/src/Slate.js
 */
const instantReplaceWord = (editor, lastWord) => {
  if (!lastWord || lastWord.length < 5) {
    return;
  }
  const href = getNormalisedUrl(lastWord);
  if (href != null) {
    editor.moveFocusBackward(lastWord.length); // select last word
    editor.unwrapInline(type); // remove existing urls
    editor.wrapInline(LinkUtils.createLink({ href }));
    editor.moveFocusForward(lastWord.length); //
  }
};

/**
 * Slate plugins to handle links.
 */
const LinkPlugins: Plugin[] = [
  AddIsToggledQuery(type, isLink),
  renderLink,
  linkOnInsertPlugin,
];

export const Link: Plugin[] = LinkPlugins;
